import { cva } from "class-variance-authority";
import { useState, type ComponentProps, type KeyboardEvent } from "react";
import { formatCount } from "@/lib/format-count";
import { cn } from "@/lib/cn";
import { Button } from "./button";
import { chipVariants } from "./chip-variants";
import { Icons } from "./icons";
import { useSlidingSelection } from "./use-sliding-selection";
import "./control-motion-recipes.css";

const groupVariants = cva("items-center", {
  variants: {
    variant: {
      /* Equal widths on a field track (Settings › Skills): every segment as
       *  wide as the longest label, so no language's label is cut. */
      segmented:
        "grid grid-flow-col auto-cols-fr gap-0.5 rounded-pill bg-input-fill p-0.5",
      /* Free widths, no track (search hub filters). */
      chips: "flex flex-wrap gap-1",
    },
  },
});

/** One choice out of a few, as 28px capsules (a switch or a filter row). */
export function ChipGroup<T extends string>({
  value,
  onChange,
  options,
  variant,
  ariaLabel,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{
    value: T;
    label: string;
    count?: number;
    testId: string;
  }>;
  variant: "segmented" | "chips";
  ariaLabel: string;
  className?: string;
}) {
  const segmented = variant === "segmented";
  const rootRef = useSlidingSelection(
    value,
    segmented,
    JSON.stringify(options),
  );
  // A segmented control steps its choice with ← and →, focus following.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const current = options.findIndex((option) => option.value === value);
    const next =
      options[current + (event.key === "ArrowRight" ? 1 : -1)] ?? null;
    if (!next) return;
    event.preventDefault();
    onChange(next.value);
    rootRef.current
      ?.querySelector<HTMLButtonElement>(`[data-testid="${next.testId}"]`)
      ?.focus();
  };
  return (
    <div
      ref={rootRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        groupVariants({ variant }),
        segmented && "denker-segmented-motion",
        className,
      )}
      onKeyDown={segmented ? handleKeyDown : undefined}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={chipVariants({ variant })}
          data-testid={option.testId}
        >
          <span className="truncate">{option.label}</span>
          {option.count !== undefined ? (
            <span className="tabular-nums text-muted">
              {formatCount(option.count)}
            </span>
          ) : null}
        </button>
      ))}
      {segmented ? (
        <span className="denker-segmented-indicator" aria-hidden="true" />
      ) : null}
    </div>
  );
}

/** One on/off chip on its own, in the free-width chips look (prompt chips
 *  in Settings › Voice). Other button props pass through, so it can be a
 *  menu trigger. */
export function ToggleChip({
  pressed,
  testId,
  className,
  children,
  ...props
}: Omit<ComponentProps<"button">, "aria-pressed"> & {
  pressed: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      {...props}
      aria-pressed={pressed}
      className={cn(chipVariants({ variant: "chips" }), className)}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/** A chip in the selected look with its own remove button (a word's
 *  mishearings in Settings › Voice › Vocabulary). A muted `note` follows the
 *  label; without `onRemove` the chip cannot be removed and has no button. */
export function RemovableChip({
  testId,
  label,
  note,
  onRemove,
  removeLabel,
}: {
  testId: string;
  label: string;
  note?: string;
  onRemove?: () => void;
  removeLabel: string;
}) {
  return (
    <span
      className={cn(
        chipVariants({ variant: "chips" }),
        "bg-[var(--color-surface-selected)] text-primary",
      )}
      data-testid={testId}
    >
      <span className="truncate">{label}</span>
      {note && <span className="shrink-0 text-muted">· {note}</span>}
      {onRemove && (
        <Button
          variant="ghost"
          size="iconXs"
          className="-mr-1 hover:bg-[var(--color-interactive-hover)]"
          onClick={onRemove}
          aria-label={removeLabel}
          data-testid={`${testId}-remove`}
        >
          <Icons.X className="h-3 w-3" aria-hidden="true" />
        </Button>
      )}
    </span>
  );
}

/** A chip that takes text: Enter adds what was typed and keeps the field
 *  focused for the next one. */
export function ChipTextInput({
  testId,
  placeholder,
  onSubmit,
}: {
  testId: string;
  placeholder: string;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState("");
  return (
    <label
      className={cn(
        chipVariants({ variant: "chips" }),
        "cursor-text bg-btn-ghost-fill hover:bg-[var(--color-interactive-hover)] focus-within:text-primary",
      )}
    >
      <Icons.Plus className="h-3 w-3 shrink-0" aria-hidden="true" />
      <input
        value={text}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          const value = text.trim();
          if (!value) return;
          onSubmit(value);
          setText("");
        }}
        className="w-[90px] border-0 bg-transparent text-appkit-caption text-primary outline-none placeholder:text-muted"
        data-testid={testId}
      />
    </label>
  );
}
